import { useMemo } from 'react';
import {
    ClipboardTextIcon,
    FilePlusIcon,
    PlusIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
    ActionBar,
    AnimatedActionBarModeTransition,
    SelectionBar,
} from '@/components';
import type { ActionConfig } from '@/components/ActionBar';
import {
    ActionType,
    DividerOrientation,
    buildSortAction,
    buildMultiSelectToggle,
} from '@/components/ActionBar';
import { Permission } from '@/types/permissions';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { ReportSortOption } from '../hooks/useReportsFilters';
import { REPORT_SORT_OPTIONS } from '../hooks/useReportsFilters';
import type { ProcedureTemplate } from '@/types/procedure';

interface ReportsActionBarProps {
    isMultiSelectMode: boolean;
    selectedCount: number;
    totalCount: number;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
    searchPlaceholder?: string;
    onExitMultiSelect: () => void;
    onSelectAll: () => void;
    bulkActions?: ActionConfig[];
    enterMultiSelectMode: () => void;
    sortOption: ReportSortOption | null;
    onSortOptionChange: (option: ReportSortOption | null) => void;
    procedureTemplates: ProcedureTemplate[];
    onCreateReport: (template?: ProcedureTemplate) => void;
}

export function ReportsActionBar({
    isMultiSelectMode,
    selectedCount,
    totalCount,
    searchValue,
    onSearchChange,
    onSearchSubmit,
    searchPlaceholder,
    onExitMultiSelect,
    onSelectAll,
    bulkActions,
    enterMultiSelectMode,
    sortOption,
    onSortOptionChange,
    procedureTemplates,
    onCreateReport,
}: ReportsActionBarProps) {
    const { t } = useTranslation([I18nNamespace.REPORTS, I18nNamespace.COMMON]);

    const reportCreateMenuItems = useMemo(() => {
        const fromTemplates = procedureTemplates.map((template) => ({
            icon: ClipboardTextIcon,
            label: template.name,
            onClick: () => onCreateReport(template),
            testId: `create-report-menu-template-${template.id}`,
        }));
        const blank = {
            icon: FilePlusIcon,
            label: t('createMenu.blankReport'),
            onClick: () => onCreateReport(),
            testId: 'create-report-menu-blank',
        };
        if (fromTemplates.length === 0) {
            return [blank];
        }
        return [...fromTemplates, { separator: true as const }, blank];
    }, [procedureTemplates, onCreateReport, t]);

    const currentSortId = sortOption
        ? `${sortOption.field}-${sortOption.direction}`
        : null;

    const browseActions: ActionConfig[] = [
        ...(onSearchChange
            ? [
                  {
                      type: ActionType.SEARCH as const,
                      value: searchValue ?? '',
                      onChange: onSearchChange,
                      onSubmit: onSearchSubmit,
                      placeholder: searchPlaceholder,
                  },
              ]
            : []),
        { type: ActionType.DIVIDER, orientation: DividerOrientation.VERTICAL },
        buildSortAction(
            t,
            REPORT_SORT_OPTIONS.map((option) => ({
                label: t(option.labelKey),
                isActive: currentSortId === option.id,
                onClick: () =>
                    onSortOptionChange({
                        field: option.field,
                        direction: option.direction,
                    }),
            })),
        ),
        buildMultiSelectToggle(t, enterMultiSelectMode),
        {
            type: ActionType.MENU,
            icon: PlusIcon,
            label: t('actions.create'),
            permission: Permission.CREATE,
            items: reportCreateMenuItems,
            triggerTestId: 'reports-create-menu',
        },
    ];

    return (
        <div className="w-fit">
            <AnimatedActionBarModeTransition
                isMultiSelectMode={isMultiSelectMode}
                browseSlot={<ActionBar actions={browseActions} />}
                selectionSlot={
                    <SelectionBar
                        selectedCount={selectedCount}
                        totalCount={totalCount}
                        entityLabel={t('selection.entity')}
                        onSelectAll={onSelectAll}
                        onExitMultiSelect={onExitMultiSelect}
                        bulkActions={bulkActions}
                    />
                }
            />
        </div>
    );
}
